import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FaInfoCircle, FaHandPaper, FaMagic } from 'react-icons/fa';
import {  updatePaymentDiscountById, getDiscountTimeframesById} from '../../../../../../../../crud/payments_api/payment_discounts';
import { Discount, TimeFrame } from '../../../../types';
import { toast } from 'react-toastify';
import { X } from 'react-bootstrap-icons';
import './UpdateDiscount.scss';
import DiscountType from '../CreateDiscount/components/DiscountType/DiscountType';
import DiscountLimits from '../CreateDiscount/components/DiscountLimits/DiscountLimits';
import DiscountScopeDisplay from './components/DiscountScopeDisplay/DiscountScopeDisplay';
import ManageDiscountItems from './components/ManageDiscountItems/ManageDiscountItems';
import ManageDiscountCategories from './components/ManageDiscountCategories/ManageDiscountCategories';
import TimeframeConfiguration from '../shared/TimeframeConfiguration/TimeframeConfiguration';
import { getItemOptions, getCategoryOptions } from '../../../utils';


interface RenderUpdateDiscountsModalProps {
  show: boolean;
  handleClose: () => void;
  discount: Discount;
  setDiscount: any;
  discounts: Discount[];
}



const UpdateDiscount: React.FC<RenderUpdateDiscountsModalProps> = ({
  show,
  handleClose,
  discount,
  setDiscount,
  discounts,
}) => {
  // Estados principales, inicializados con los valores del descuento recibido
  const [discountName, setDiscountName] = useState(discount?.payment_discount_name || '');
  const [discountType, setDiscountType] = useState(discount?.payment_discount_percentage ? 'Percentage' : 'Amount');
  const [discountAmount, setDiscountAmount] = useState<number | null>(discount?.payment_discount_amount ?? null);
  const [discountPercentage, setDiscountPercentage] = useState<number | null>(discount?.payment_discount_percentage ?? null);
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const [amountError, setAmountError] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Available options for multi-select
  const [itemOptions, setItemOptions] = useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  
  // Timeframe configuration state
  const [alwaysActive, setAlwaysActive] = useState(true);
  const [timeframes, setTimeframes] = useState<TimeFrame[]>([]);
  const [isLoadingTimeframes, setIsLoadingTimeframes] = useState(false);
  
  // Manual application state
  const [isManual, setIsManual] = useState(discount?.is_manual || false);
  
  // Track items/categories to add/remove
  const [itemsToAdd, setItemsToAdd] = useState<number[]>([]);
  const [itemsToRemove, setItemsToRemove] = useState<number[]>([]);
  const [categoriesToAdd, setCategoriesToAdd] = useState<number[]>([]);
  const [categoriesToRemove, setCategoriesToRemove] = useState<number[]>([]);
  
  // Fecha interáctiva en vez de string
const [selectedExpirationDate, setSelectedExpirationDate] = useState<Date | null>(
  discount?.expiration_date ? new Date(discount.expiration_date) : null
);
const [showCalendar, setShowCalendar] = useState(false);

  const [limitType, setLimitType] = useState('Sin limite');
  const [isGlobal, setIsGlobal] = useState(discount?.isglobal || false);
  const [menuId, setMenuId] = useState(discount?.menu_id || '');
  const [categoryId, setCategoryId] = useState(discount?.menu_category_id || '');
  const [itemId, setItemId] = useState(discount?.item_id || '');
  const [maxNumber, setMaxNumber] = useState<number | null>(discount?.max_number ?? null);
  const [maxAmount, setMaxAmount] = useState<number | null>(discount?.max_budget ?? null);

  // Fetch item and category options
  useEffect(() => {
    const fetchOptions = async () => {
      if (!show) return;
      
      setIsLoadingOptions(true);
      try {
        const items = await getItemOptions();
        const categories = await getCategoryOptions();
        if (items) setItemOptions(items);
        if (categories) setCategoryOptions(categories);
      } catch (error) {
        console.error('Error fetching options:', error);
        toast.error('Error al cargar opciones de ítems y categorías');
      } finally {
        setIsLoadingOptions(false);
      }
    };

    if (show) {
      fetchOptions();
    }
  }, [show]);

  // Fetch timeframe configuration when discount changes
  useEffect(() => {
    const fetchTimeframes = async () => {
      if (!show || !discount?.payment_discount_id) return;
      
      setIsLoadingTimeframes(true);
      try {
        // Set always_active from discount if available
        if (discount.always_active !== undefined) {
          setAlwaysActive(discount.always_active);
        }
        
        // Fetch timeframes from backend
        const response = await getDiscountTimeframesById(discount.payment_discount_id);
        
        // The API returns frames directly in response.data, not nested in timeframes_config
        if (response?.data?.frames && Array.isArray(response.data.frames)) {
          setTimeframes(response.data.frames);
        } else {
          setTimeframes([]);
        }
      } catch (error) {
        console.error('Error fetching timeframes:', error);
        // Don't show error toast - timeframes might not exist for all discounts
        setTimeframes([]);
      } finally {
        setIsLoadingTimeframes(false);
      }
    };

    if (show && discount) {
      fetchTimeframes();
    }
  }, [show, discount]);

  useEffect(() => {
    if (discount) {
      setDiscountName(discount.payment_discount_name || '');
      setDiscountType(discount.payment_discount_percentage ? 'Percentage' : 'Amount');
      setDiscountAmount(discount.payment_discount_amount ?? null);
      setDiscountPercentage(discount.payment_discount_percentage ?? null);
      
      // Validate the date before creating a Date object
      if (discount.expiration_date) {
        const dateObj = new Date(discount.expiration_date);
        setSelectedExpirationDate(!isNaN(dateObj.getTime()) ? dateObj : null);
      } else {
        setSelectedExpirationDate(null);
      }
      
      // Determine limit type based on what's set - using the correct strings from DiscountLimits component
      let determinedLimitType = 'Sin limite';
      
      if (discount.expiration_date && discount.max_budget) {
        determinedLimitType = 'Monto Máximo y Fecha';
      } else if (discount.expiration_date) {
        determinedLimitType = 'Fecha limite';
      } else if (discount.max_budget) {
        determinedLimitType = 'Presupuesto máximo';
      } else if (discount.max_number) {
        determinedLimitType = 'Número de usos';
      }
      
      console.log('Discount data:', {
        expiration_date: discount.expiration_date,
        max_budget: discount.max_budget,
        max_number: discount.max_number,
        determinedLimitType
      });
      
      setLimitType(determinedLimitType);
      setIsGlobal(discount.isglobal || false);
      setMenuId(discount.menu_id || '');
      setCategoryId(discount.menu_category_id || '');
      setItemId(discount.item_id || '');
      setMaxNumber(discount.max_number ?? null);
      setMaxAmount(discount.max_budget ?? null);
      
      // Set always_active from discount
      if (discount.always_active !== undefined) {
        setAlwaysActive(discount.always_active);
      }
      
      // Set is_manual from discount
      setIsManual(discount.is_manual || false);
    }
  }, [discount]);

  // Validación del monto - Note: Price validation removed to match CreateCoupon structure
  // Backend will handle validation of discount amount vs item price
  useEffect(() => {
    if (discountType === 'Amount' && discountAmount !== null) {
      // Basic validation for positive amounts
      if (discountAmount <= 0) {
        setAmountError('El monto del descuento debe ser mayor que 0.');
      } else {
        setAmountError('');
      }
    } else {
      setAmountError('');
    }
  }, [discountAmount, discountType]);

  // Handler for adding/removing item
  const handleAddItem = (itemId: number) => {
    if (itemsToAdd.includes(itemId)) {
      // Remove from add list (cancel addition)
      setItemsToAdd(itemsToAdd.filter(id => id !== itemId));
    } else {
      // Add to add list
      setItemsToAdd([...itemsToAdd, itemId]);
    }
  };

  // Handler for removing item (handles both existing items and canceling additions)
  const handleRemoveItem = (itemId: number) => {
    // Check if this is a newly added item (not yet in the discount)
    if (itemsToAdd.includes(itemId)) {
      // Cancel the addition
      setItemsToAdd(itemsToAdd.filter(id => id !== itemId));
    } else {
      // This is an existing item, toggle removal
      if (itemsToRemove.includes(itemId)) {
        // Remove from remove list (cancel removal)
        setItemsToRemove(itemsToRemove.filter(id => id !== itemId));
      } else {
        // Add to remove list
        setItemsToRemove([...itemsToRemove, itemId]);
      }
    }
  };

  // Handler for adding/removing category
  const handleAddCategory = (categoryId: number) => {
    if (categoriesToAdd.includes(categoryId)) {
      // Remove from add list (cancel addition)
      setCategoriesToAdd(categoriesToAdd.filter(id => id !== categoryId));
    } else {
      // Add to add list
      setCategoriesToAdd([...categoriesToAdd, categoryId]);
    }
  };

  // Handler for removing category (handles both existing categories and canceling additions)
  const handleRemoveCategory = (categoryId: number) => {
    // Check if this is a newly added category (not yet in the discount)
    if (categoriesToAdd.includes(categoryId)) {
      // Cancel the addition
      setCategoriesToAdd(categoriesToAdd.filter(id => id !== categoryId));
    } else {
      // This is an existing category, toggle removal
      if (categoriesToRemove.includes(categoryId)) {
        // Remove from remove list (cancel removal)
        setCategoriesToRemove(categoriesToRemove.filter(id => id !== categoryId));
      } else {
        // Add to remove list
        setCategoriesToRemove([...categoriesToRemove, categoryId]);
      }
    }
  };

  // Reset all pending changes
  const handleReset = () => {
    setItemsToAdd([]);
    setItemsToRemove([]);
    setCategoriesToAdd([]);
    setCategoriesToRemove([]);
    toast.info('Cambios restablecidos');
  };

  const handleUpdateDiscount = async () => {
    // Validate timeframes if not always active
    if (!alwaysActive && timeframes.length === 0) {
      toast.error('Debes configurar al menos un horario cuando el descuento no está siempre activo.');
      return;
    }

    // Validate each timeframe has at least one day selected
    if (!alwaysActive) {
      const invalidTimeframe = timeframes.find(tf => tf.days.length === 0);
      if (invalidTimeframe) {
        toast.error(`El horario "${invalidTimeframe.name}" debe tener al menos un día seleccionado.`);
        return;
      }
    }

    setIsUpdating(true);
    try {
      // Convertir la fecha seleccionada a formato "YYYY-MM-DD"
      // Validate the date before converting to ISO string
      const formattedExpiration = selectedExpirationDate && !isNaN(selectedExpirationDate.getTime())
        ? selectedExpirationDate.toISOString().split('T')[0]
        : '';
      const payload: any = {
        payment_discount_name: discountName,
        payment_discount_amount: discountAmount,
        payment_discount_percentage: discountPercentage,
        expiration_date: formattedExpiration === '' ? null : formattedExpiration,
        isglobal: isGlobal,
        menu_id: menuId === '' ? null : menuId,
        menu_category_id: categoryId === '' ? null : categoryId,
        item_id: itemId === '' ? null : itemId,
        max_number: maxNumber,
        max_budget: maxAmount,
        // Add timeframe configuration
        always_active: alwaysActive,
        timeframes_config: !alwaysActive && timeframes.length > 0 ? {
          frames: timeframes.map(tf => ({
            name: tf.name,
            days: tf.days,
            start_time: tf.start_time,
            end_time: tf.end_time,
            active: tf.active ?? true
          }))
        } : undefined,
        // Add granular updates
        items_added: itemsToAdd.length > 0 ? itemsToAdd : undefined,
        items_removed: itemsToRemove.length > 0 ? itemsToRemove : undefined,
        categories_added: categoriesToAdd.length > 0 ? categoriesToAdd : undefined,
        categories_removed: categoriesToRemove.length > 0 ? categoriesToRemove : undefined,
        // Manual application
        is_manual: isManual,
      };
      if (discountType === 'Percentage') {
        payload.payment_discount_amount = null;
      } else {
        payload.payment_discount_percentage = null;
      }
      // Handle limit types using the correct strings from DiscountLimits component
      if (limitType === 'Fecha limite') {
        payload.max_number = null;
        payload.max_budget = null;
      } else if (limitType === 'Presupuesto máximo') {
        payload.expiration_date = null;
        payload.max_number = null;
      } else if (limitType === 'Número de usos') {
        payload.expiration_date = null;
        payload.max_budget = null;
      } else if (limitType === 'Monto Máximo y Fecha') {
        payload.max_number = null;
      } else if (limitType === 'Sin limite') {
        payload.expiration_date = null;
        payload.max_number = null;
        payload.max_budget = null;
      }
      const response = await updatePaymentDiscountById(discount.payment_discount_id, payload);
      const newDiscount = { id: new Date().getTime(), ...(response as any).data };
      const updatedDiscounts = discounts.map((d) =>
        d.payment_discount_id === discount.payment_discount_id ? newDiscount : d
      );
      setDiscount(updatedDiscounts);
      
      // Reset granular update states
      setItemsToAdd([]);
      setItemsToRemove([]);
      setCategoriesToAdd([]);
      setCategoriesToRemove([]);
      
      toast.success('Descuento actualizado correctamente');
      handleClose();
    } catch (error: any) {
      if (error?.response?.data?.message === 'The discount amount is greater than the price of the item') {
        setAmountError('El monto del descuento no puede ser mayor o igual al valor del ítem seleccionado.');
      } else {
        console.error('Error actualizando el descuento:', error);
        toast.error(`Error actualizando el descuento: ${error?.response?.data?.message || error.message}`);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} className="modal-descuentos" size="lg">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Actualizar Descuento</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        {/* Banner informativo */}
        <div className="discount-info-banner">
          <div className="banner-header" onClick={() => setShowInfoBanner(!showInfoBanner)}>
            <div className="header-content">
              <div className="header-icon">
                <FaInfoCircle />
              </div>
              <div className="header-title">¿Qué es un descuento?</div>
            </div>
            <div className={`toggle-icon ${showInfoBanner ? 'expanded' : 'collapsed'}`}>
              {showInfoBanner ? '−' : '+'}
            </div>
          </div>
          {showInfoBanner && (
            <div className="banner-content">
              Un descuento es una reducción aplicada automáticamente al precio de productos o servicios cuando están activos. 
              A diferencia de los cupones, los descuentos se aplican de forma <strong>automática</strong> a todos los clientes que cumplan las condiciones establecidas.
            </div>
          )}
        </div>

        <Form className="discount-form">{/* onSubmit={handleUpdateDiscount} */}
          {/* Nombre del descuento */}
          <div className="form-section name-section">
            <Form.Group className="mp-formgroup">
              <Form.Control
                type="text"
                value={discountName}
                onChange={(e) => setDiscountName(e.target.value)}
                required
                placeholder="Ingrese el descuento"
                className="discount-name-input"
              />
            </Form.Group>
          </div>

          {/* Tipo de descuento modularizado */}
          <DiscountType
            discountType={discountType}
            setDiscountType={setDiscountType}
            discountAmount={discountAmount}
            setDiscountAmount={setDiscountAmount}
            discountPercentage={discountPercentage}
            setDiscountPercentage={setDiscountPercentage}
            amountError={amountError}
          />

          <DiscountLimits
            limitType={limitType}
            setLimitType={setLimitType}
            selectedExpirationDate={selectedExpirationDate}
            setSelectedExpirationDate={setSelectedExpirationDate}
            showCalendar={showCalendar}
            setShowCalendar={setShowCalendar}
            maxBudget={maxAmount}
            setMaxBudget={setMaxAmount}
            maxNumber={maxNumber}
            setMaxNumber={setMaxNumber}
          />

          {/* Scope Display Section */}
          <DiscountScopeDisplay
            scopeType={discount?.scope_type || (discount?.isglobal ? 'global' : 'item')}
            itemsDetail={discount?.items_detail || []}
            categoriesDetail={discount?.categories_detail || []}
            menuDetail={discount?.menu_detail || null}
            isglobal={discount?.isglobal}
            onRemoveItem={handleRemoveItem}
            onRemoveCategory={handleRemoveCategory}
            itemsToRemove={itemsToRemove}
            categoriesToRemove={categoriesToRemove}
            itemsToAdd={itemsToAdd}
            categoriesToAdd={categoriesToAdd}
            availableItems={itemOptions}
            availableCategories={categoryOptions}
          />

          {/* Granular Item Management - only for multi_item discounts */}
          {(discount?.scope_type === 'multi_item' || (discount?.item_ids && discount.item_ids.length > 1)) && (
            isLoadingOptions ? (
              <div className="loading-options-container">
                <Spinner animation="border" variant="warning" size="sm" />
                <span className="loading-text">Cargando ítems disponibles...</span>
              </div>
            ) : (
              <ManageDiscountItems
                currentItems={discount?.items_detail || []}
                availableItems={itemOptions}
                itemsToAdd={itemsToAdd}
                itemsToRemove={itemsToRemove}
                onAddItem={handleAddItem}
                onRemoveItem={handleRemoveItem}
              />
            )
          )}

          {/* Granular Category Management - only for multi_category discounts */}
          {(discount?.scope_type === 'multi_category' || (discount?.category_ids && discount.category_ids.length > 1)) && (
            isLoadingOptions ? (
              <div className="loading-options-container">
                <Spinner animation="border" variant="warning" size="sm" />
                <span className="loading-text">Cargando categorías disponibles...</span>
              </div>
            ) : (
              <ManageDiscountCategories
                currentCategories={discount?.categories_detail || []}
                availableCategories={categoryOptions}
                categoriesToAdd={categoriesToAdd}
                categoriesToRemove={categoriesToRemove}
                onAddCategory={handleAddCategory}
                onRemoveCategory={handleRemoveCategory}
              />
            )
          )}

          {/* Timeframe Configuration Section - LAST SECTION */}
          {isLoadingTimeframes ? (
            <div className="loading-timeframes-container">
              <Spinner animation="border" variant="warning" size="sm" />
              <span className="loading-text">Cargando configuración de horarios...</span>
            </div>
          ) : (
            <TimeframeConfiguration
              alwaysActive={alwaysActive}
              setAlwaysActive={setAlwaysActive}
              timeframes={timeframes}
              setTimeframes={setTimeframes}
            />
          )}

          {/* Manual Application Section 

          <div className="form-section application-mode-section">
            <div className="section-title">
              <FaHandPaper className="section-icon" />
              Modo de Aplicación
            </div>
            <div className="application-mode-options">
              <div
                className={`application-mode-option ${!isManual ? 'active' : ''}`}
                onClick={() => setIsManual(false)}
              >
                <div className="mode-header">
                  <div className="mode-icon">
                    <FaMagic />
                  </div>
                  <div className="mode-title">Automático</div>
                </div>
                <div className="mode-description">Se aplica automáticamente al generar la boleta</div>
              </div>
              <div
                className={`application-mode-option ${isManual ? 'active' : ''}`}
                onClick={() => setIsManual(true)}
              >
                <div className="mode-header">
                  <div className="mode-icon">
                    <FaHandPaper />
                  </div>
                  <div className="mode-title">Manual</div>
                </div>
                <div className="mode-description">El mesero o cajero debe aplicarlo manualmente</div>
              </div>
            </div>
          </div>*/}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          {/* Reset button - only show if there are pending changes */}
          {(itemsToAdd.length > 0 || itemsToRemove.length > 0 || categoriesToAdd.length > 0 || categoriesToRemove.length > 0) && (
            <Button
              className="form-button-reset"
              variant="outline-secondary"
              onClick={handleReset}
              disabled={isUpdating}
            >
              Restablecer
            </Button>
          )}
          
          <Button
            className="form-button-create"
            variant="none"
            onClick={() => {
              if (!discountType) {
                toast.error('Debes seleccionar el tipo de descuento.');
                return;
              }
              if (!!amountError) {
                toast.error('Corrige el error en el monto del descuento.');
                return;
              }
              handleUpdateDiscount();
            }}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Actualizando...
              </>
            ) : (
              'Actualizar Descuento'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};


export default UpdateDiscount;